import { MouseEvent, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { SxProps } from "@mui/material/styles";

import { type Patient } from "services/types/patients.type";
import { formatPhoneNumber } from "utils/common";

import CallButton from "./call-button.component";
import useMakeCall, { useDialDeviceTooltip } from "./use-make-call.hook";

type CallPatientNumberButtonProps = {
  phoneNumber?: string;
  patient?: Partial<Patient>;
  size?: "small" | "medium";
  title?: string;
  redirect?: boolean;
  sx?: SxProps;
};
export default function CallPatientNumberButton({
  phoneNumber,
  patient,
  title,
  redirect,
  size = "medium",
  sx
}: CallPatientNumberButtonProps) {
  const navigate = useNavigate();
  const handleMakeCall = useMakeCall();
  const tooltip = useDialDeviceTooltip(
    `Call ${title || ""} ${formatPhoneNumber(phoneNumber ?? "")}`
  );

  const handleCallButtonClick = useCallback(
    async (event: MouseEvent) => {
      // Read before the timeout: React's synthetic event is gone by then.
      const altKey = event.altKey;
      if (redirect && patient?.id) {
        navigate(`/patients/${patient.id}/`);
      }
      setTimeout(async () => {
        handleMakeCall(phoneNumber ?? patient?.phone ?? "", patient, {
          altKey
        });
      }, 1);
    },
    [handleMakeCall, navigate, patient, phoneNumber, redirect]
  );

  return (
    <Tooltip disableInteractive title={tooltip}>
      <Box>
        <CallButton
          sx={{
            transform: size === "medium" ? "scale(0.75)" : "scale(0.6)",
            ...sx
          }}
          onClick={handleCallButtonClick}
        />
      </Box>
    </Tooltip>
  );
}
